import React, { useState, useMemo } from 'react';
import type { GroupInformationData, GroupMember } from '../../../types/customer-portal';
import GroupMemberModal from './GroupMemberModal';
import Pagination from '../../../../../shared/components/ui/Pagination';
import './GroupMembersInformationStep.css';

interface GroupMembersInformationStepProps {
  data?: GroupInformationData;
  onNext: (data: GroupInformationData) => void;
  onBack: () => void;
}

const GroupMembersInformationStep: React.FC<GroupMembersInformationStepProps> = ({
  data,
  onNext,
  onBack
}) => {
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>(
    data?.groupMembers || []
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<GroupMember | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Address sharing state management
  const [firstMemberAddress, setFirstMemberAddress] = useState<{
    houseUnitNo?: string;
    streetName?: string;
    subdivisionVillage?: string;
    barangay?: string;
    city?: string;
    province?: string;
    zipCode?: string;
    country?: string;
  } | null>(null);
  const [shareAddressEnabled, setShareAddressEnabled] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const membersPerPage = 5;

  const maxMembers = 15;
  const canAddMore = groupMembers.length < maxMembers;
  
  // Pagination calculations
  const totalPages = Math.ceil(groupMembers.length / membersPerPage);
  const startIndex = (currentPage - 1) * membersPerPage;
  const endIndex = startIndex + membersPerPage;
  const currentMembers = groupMembers.slice(startIndex, endIndex);

  // Form validation logic
  const isFormValid = useMemo(() => {
    return groupMembers.length > 0;
  }, [groupMembers]);

  const handleAddMember = () => {
    if (canAddMore) {
      setEditingMember(null);
      setIsEditMode(false);
      setIsModalOpen(true);
    }
  };

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEditMember = (member: GroupMember) => {
    setEditingMember(member);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleSaveMember = (member: GroupMember) => {
    if (isEditMode && editingMember) {
      // Update existing member - stay on current page
      setGroupMembers(prev => {
        const updatedMembers = prev.map(m => m.id === member.id ? member : m);
        
        // If updating the first member and address sharing is enabled, update the first member address
        if (prev.length > 0 && prev[0].id === member.id && member.shareAddressWithGroupMembers) {
          setFirstMemberAddress({
            houseUnitNo: member.houseUnitNo,
            streetName: member.streetName,
            subdivisionVillage: member.subdivisionVillage,
            barangay: member.barangay,
            city: member.city,
            province: member.province,
            zipCode: member.zipCode,
            country: member.country
          });
          setShareAddressEnabled(true);
        }
        
        return updatedMembers;
      });
    } else {
      // Add new member - navigate to the page containing the new member
      setGroupMembers(prev => {
        const newMembers = [...prev, member];
        const newMemberPage = Math.ceil(newMembers.length / membersPerPage);
        setCurrentPage(newMemberPage);
        
        // If this is the first member, store their address and sharing preference
        if (prev.length === 0) {
          if (member.shareAddressWithGroupMembers) {
            setFirstMemberAddress({
              houseUnitNo: member.houseUnitNo,
              streetName: member.streetName,
              subdivisionVillage: member.subdivisionVillage,
              barangay: member.barangay,
              city: member.city,
              province: member.province,
              zipCode: member.zipCode,
              country: member.country
            });
            setShareAddressEnabled(true);
          } else {
            setShareAddressEnabled(false);
            setFirstMemberAddress(null);
          }
        }
        
        return newMembers;
      });
    }
  };

  const handleDeleteMember = (memberId: string) => {
    setGroupMembers(prev => {
      const newMembers = prev.filter(m => m.id !== memberId);
      const newTotalPages = Math.ceil(newMembers.length / membersPerPage);
      
      // If current page is now beyond available pages, go to last page
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      } else if (newMembers.length === 0) {
        setCurrentPage(1);
      }
      
      return newMembers;
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
    setIsEditMode(false);
  };

  const handleNext = () => {
    if (isFormValid) {
      onNext({ groupMembers });
    }
  };

  const formatMemberName = (member: GroupMember) => {
    const parts = [member.title, member.firstName];
    if (member.middleName) {
      parts.push(member.middleName.charAt(0) + '.');
    }
    parts.push(member.lastName);
    if (member.suffix && member.suffix !== 'N/A') {
      parts.push(member.suffix);
    }
    return parts.join(' ');
  };

  return (
    <>
      <div className="group-members-step">
        <div className="group-members-step-header">
          <div className="step-header-content">
            <h3 className="step-title">Group Member Information</h3>
            <p className="step-description">
              List up to 15 individuals who will be part of this plan.
            </p>
          </div>
          {canAddMore && (
            <button
              className="add-member-btn"
              onClick={handleAddMember}
              aria-label="Add group member"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          )}
        </div>

        <div className="step-content">
          {groupMembers.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-text">
                No group members added yet. Click the + button to add a member.
              </p>
            </div>
          ) : (
            <>
              <div className="members-list">
                {currentMembers.map((member) => (
                  <div key={member.id} className="member-card">
                    <div className="member-info">
                      <h4 className="member-name">
                        {formatMemberName(member)}
                      </h4>
                      <p className="member-type">Group Member</p>
                    </div>
                    <button
                      className="edit-member-btn"
                      onClick={() => handleEditMember(member)}
                      aria-label={`Edit ${formatMemberName(member)}`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              {/* Pagination Component */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                className="group-members-pagination"
              />
            </>
          )}

          {groupMembers.length >= maxMembers && (
            <div className="max-members-notice">
              <p>You've reached the limit of {maxMembers} group members.</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button
          type="button"
          className="btn-back"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className={`btn-next ${!isFormValid ? 'disabled' : ''}`}
          onClick={handleNext}
          disabled={!isFormValid}
        >
          Next
        </button>
      </div>

      {/* Group Member Modal */}
      <GroupMemberModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveMember}
        onDelete={handleDeleteMember}
        member={editingMember}
        isEditMode={isEditMode}
        isFirstMember={groupMembers.length === 0 && !isEditMode}
        firstMemberAddress={shareAddressEnabled ? firstMemberAddress : null}
        shareAddressEnabled={shareAddressEnabled}
      />
    </>
  );
};

export default GroupMembersInformationStep;